import { uniq } from 'lodash';
import sortBy from 'lodash/sortBy';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { formatDayFromUtc } from '../../../utils/date';
import { roundValue } from '../../../utils/math';
import { queryHashGenerator } from '../../../utils/queryHash';
import { EntityType, IComputedAggregate, ISelectableTag } from '../../types';
import {
  crossTestAggregates,
  ICrossTestAggregates,
} from '../crossTestAggregatesProvider';
import { IInclusiveDateOption } from '../settings/dates/dateRangeProvider';
import {
  dsiReportSettingsProvider,
  IDsiReportSettings,
} from '../settings/reports/dsiReportSettingsProvider';
import { formatTimeframe } from '../utils/formatTimeframe';
import { isValidDateRange } from '../utils/isValidDateRange';
import { IReportResult, IReportState } from './types';

const cmjId = '7nNduHeM5zETPjHxvm7s';
const isoId = '2uS5XD5kXmWgIZ5HhQ3A';

export interface IDsi {
  timestamp: number;
  dsi: number;

  isoPeak: {
    value: number;
    stale: boolean;
    timestamp: number;
  };
  cmjPeak: {
    value: number;
    stale: boolean;
    timestamp: number;
  };
}

export interface IDsiResult extends IReportResult {
  dsi: number;
  dsiPercentageChange: number | null;
  dsiTotalChange: number | null;
  dsiStale: boolean;
  isoPeak: number | null;
  isoStale: boolean;
  isoDate?: number;
  cmjPeak: number | null;
  cmjStale: boolean;
  cmjDate?: number;
  timestamp: number;
  displayDate: string;
}

export interface IDsiReportState extends IReportState {
  settings: IDsiReportSettings;
  dsiList: IDsi[];
  resultList: IDsiResult[];
  timeframe: string;
}

/**
 * @description pipeline that observes the aggregates requested based on the Dsi report settings
 * 	and then determines and emits the state of the report
 * @param entityType
 * @param entityId
 * @returns
 */
export const dsiReportProvider = (
  entityType: EntityType,
  entityId: string
): Observable<IDsiReportState> => {
  return dsiReportSettingsProvider().pipe(
    /// observe the aggregates as defined in the settings
    switchMap(
      (
        settings: IDsiReportSettings
      ): Observable<
        [IDsiReportSettings, ICrossTestAggregates, IHashManifest]
      > => {
        /// validate settings before fetching data
        if (validateSettings(settings).isValid) {
          const { timeframe, isoTags, cmjTags } = settings;
          /// date type is always `custom`
          const dateType: IInclusiveDateOption = {
            label: 'Custom',
            value: 'custom',
          };
          /// create query hash for iso tests
          const isoHash = queryHashGenerator(
            isoId,
            isoTags.map((t: ISelectableTag): string => t.value)
          );
          /// create query hash for cmj tests
          const cmjHash = queryHashGenerator(
            cmjId,
            cmjTags.map((t: ISelectableTag): string => t.value)
          );

          const hashManifest: IHashManifest = {
            cmjHash,
            isoHash,
          };

          return combineLatest<
            [IDsiReportSettings, ICrossTestAggregates, IHashManifest]
          >([
            of<IDsiReportSettings>(settings),
            crossTestAggregates(
              [isoHash, cmjHash],
              entityId,
              entityType,
              dateType,
              timeframe
            ),
            of(hashManifest),
          ]);
        } else {
          return combineLatest([of(settings), of(null), of(null)]);
        }
      }
    ),
    /// map the aggregates to a state
    map(
      (
        result: [IDsiReportSettings, ICrossTestAggregates, IHashManifest]
      ): IDsiReportState => {
        const [settings, aggregates, hashManifest] = result;
        const dsiList = generateDsiList(aggregates, hashManifest);
        const resultList = generateDsiResultList(dsiList);
        return {
          ...validateState(settings, resultList),
          settings,
          dsiList,
          resultList,
          timeframe: formatTimeframe(
            settings.timeframe?.from?.value,
            settings.timeframe?.to?.value
          ),
        };
      }
    )
  );
};
/**
 * @description generates the result list by breaking the data into quartiles
 * @param dsiList
 * @returns
 */
const generateDsiResultList = (dsiList: IDsi[]): IDsiResult[] => {
  if (dsiList.length === 0) return [];
  const sortedDsiList = sortBy(dsiList, (dsi: IDsi): number => -dsi.timestamp);
  const results: IDsiResult[] = [];

  for (let i = 0; i < sortedDsiList.length; i++) {
    const dsi = sortedDsiList[i];
    let change;
    if (i < sortedDsiList.length - 1) {
      const nearestComparableDsi = sortedDsiList
        .slice(i + 1)
        .find((d: IDsi): boolean => d.dsi !== null);
      change = calculateChange(nearestComparableDsi?.dsi, dsi?.dsi);
    }

    results.push({
      dsi: dsi.dsi,
      dsiStale: dsi.cmjPeak.stale || dsi.isoPeak.stale,
      isoPeak: dsi.isoPeak.value,
      isoStale: dsi.isoPeak.stale,
      isoDate: dsi.isoPeak.timestamp,
      cmjPeak: dsi.cmjPeak.value,
      cmjStale: dsi.cmjPeak.stale,
      cmjDate: dsi.cmjPeak.timestamp,
      timestamp: dsi.timestamp,
      displayDate: formatDayFromUtc(dsi.timestamp),
      ...change,
    });
  }
  return results;
};

/**
 * @description calculates the change in dsi values between quartiles
 * @param previousDsiStopRe
 * @param currentDsi
 * @returns
 */
const calculateChange = (
  previousDsi: number,
  currentDsi: number
): { dsiPercentageChange: number; dsiTotalChange: number } => {
  if (currentDsi !== null && previousDsi !== null) {
    const dsiTotalChange = roundValue(currentDsi - previousDsi, 2);
    return {
      dsiTotalChange,
      dsiPercentageChange: roundValue((dsiTotalChange / previousDsi) * 100, 1),
    };
  } else {
    return {
      dsiTotalChange: null,
      dsiPercentageChange: null,
    };
  }
};

/**
 * @description
 * @param aggregates
 * @param hashManifest
 * @returns
 */
const generateDsiList = (
  aggregates: ICrossTestAggregates,
  hashManifest: IHashManifest
): IDsi[] => {
  const cmjs = aggregates[hashManifest.cmjHash];
  const isos = aggregates[hashManifest.isoHash];

  const cmjSessionIds = cmjs.sessions.map(
    (ca: IComputedAggregate): string => ca.meta.id
  );

  const isoSessionIds = isos.sessions.map(
    (ca: IComputedAggregate): string => ca.meta.id
  );

  const uniqueSessionIds = uniq([...cmjSessionIds, ...isoSessionIds]);

  return uniqueSessionIds.map((sessionId: string): IDsi => {
    const sameDayCmj = cmjs.sessions.find(
      (ca: IComputedAggregate): boolean => ca.meta.id === sessionId
    );
    const closestCmj = cmjs.sessions.find(
      (session: IComputedAggregate): boolean => session.meta.id < sessionId
    );

    const sameDayIso = isos.sessions.find(
      (ca: IComputedAggregate): boolean => ca.meta.id === sessionId
    );
    const closestIso = isos.sessions.find(
      (session: IComputedAggregate): boolean => session.meta.id < sessionId
    );

    const foundCmj = sameDayCmj ?? closestCmj;
    const foundIso = sameDayIso ?? closestIso;

    const cmjPeak = Boolean(foundCmj) ? determineCmjPeakForce(foundCmj) : null;
    const isoPeak = Boolean(foundIso)
      ? foundIso.data.peakForce.topValues[0]
      : null;

    return {
      timestamp: +sessionId,
      cmjPeak: {
        value: cmjPeak ? roundValue(cmjPeak, 0) : null,
        stale: !sameDayCmj,
        timestamp: +foundCmj?.meta?.id,
      },
      isoPeak: {
        value: isoPeak ? roundValue(isoPeak, 0) : null,
        stale: !sameDayIso,
        timestamp: +foundIso?.meta?.id,
      },
      dsi: cmjPeak && isoPeak ? roundValue(cmjPeak / isoPeak, 2) : null,
    };
  });
};

/**
 * @description returns the highest peak force from either the braking or propulsive phase
 * @param aggregate
 * @returns
 */
const determineCmjPeakForce = (aggregate: IComputedAggregate): number => {
  const brakingPeak = aggregate.data.peakBrakingForce.topValues[0];
  const propulsivePeak = aggregate.data.peakPropulsiveForce.topValues[0];
  return brakingPeak >= propulsivePeak ? brakingPeak : propulsivePeak;
};

interface IValidationResult {
  isValid: boolean;
  errorCode?: string;
}

/**
 * @description validates the user settings for the report
 * @param settings
 * @returns
 */
const validateState = (
  settings: IDsiReportSettings,
  resultList: IDsiResult[]
): IValidationResult => {
  const settingsValidation = validateSettings(settings);
  if (settingsValidation.isValid) {
    if (resultList.length === 0) {
      return {
        isValid: false,
        errorCode: 'insufficient-data',
      };
    } else {
      return {
        isValid: true,
      };
    }
  } else {
    return settingsValidation;
  }
};

const validateSettings = (settings: IDsiReportSettings): IValidationResult => {
  if (
    !isValidDateRange({
      dateType: { label: 'Custom', value: 'custom' },
      dateRange: settings?.timeframe,
    })
  ) {
    return {
      isValid: false,
      errorCode: 'missing-date-range',
    };
  } else {
    return {
      isValid: true,
    };
  }
};

interface IHashManifest {
  cmjHash: string;
  isoHash: string;
}
